// Mock data for gabrielseng.com — sourced from Gabriel's resume

export const profile = {
  name: "GABRIEL SENG",
  tagline: "Mechanical Engineering · University of Virginia · Builder of Systems That Move",
  currentStatus:
    "First-year at UVA · Suspension team, Solar Car Team · Open to Summer 2026 internships",
  headshot: "/gabriel-headshot.jpg",
  resumeUrl: "/Gabriel_Seng_Resume.pdf",
};

export const marqueeKeywords = [
  "Mechanical Engineering",
  "Solar Vehicle Design",
  "UVA",
  "Fabrication",
  "CAD",
  "Systems Thinking",
  "Class of 2029",
  "SolidWorks",
  "MATLAB",
  "Research",
];

export const aboutCopy = [
  "I like taking a problem from theory to working hardware, starting from the physics, building the model, then dealing with everything that goes wrong when it meets the real world.",
  "So far that's shown up in a cart-mounted inverted pendulum controlled with LQR, where I derived the dynamics, built the state-space model, and tuned the controller myself; a shape-memory alloy actuation module, where I ran CFD to optimize cooling; and a trail-arm suspension contribution for UVA's Solar Car team. I haven't settled on a specific specialization yet, but I keep gravitating toward projects where understanding the underlying physics is what actually gets the thing working.",
];

export const stats = [
  { label: "Certification", value: "CSWA — SolidWorks" },
  { label: "GPA", value: "3.67 / 4.00" },
  { label: "Grad", value: "Class of 2029" },
  { label: "University", value: "UVA" },
  { label: "Status", value: "Open to Internships" },
];

export const skills = [
  {
    category: "CAD & Simulation",
    items: ["SolidWorks", "AutoCAD", "ANSYS", "CFD", "FEA", "Certified SolidWorks Associate"],
  },
  {
    category: "Programming & Analysis",
    items: ["MATLAB", "Python", "Java"],
  },
  {
    category: "Fabrication",
    items: ["Water-jet Cutting", "Plasma Cutting", "3D Printing (Bambu)", "Hand Tools"],
  },
  {
    category: "Collaboration",
    items: [
      "Team Leadership",
      "Conflict Resolution",
      "Attention to Detail",
      "Technical Documentation",
    ],
  },
];

export const projectFilters = ["All", "Personal", "Academic", "Club", "Research"];

export const projects = [
  {
    id: "memory-shaper",
    title: "Shape Memory Alloy (SMA) Demonstrator",
    tag: "Academic",
    summary:
      "A portable, electrically-driven module that sets and returns a Nitinol wire through controlled resistive heating and forced-air cooling.",
    oneLiner:
      "A portable, electrically-driven module that sets a Nitinol wire into a shape and returns it on demand through controlled resistive heating and forced-air cooling, replacing the bulky, liquid-cooled setups used in existing lab demonstrations.",
    thumbnail: "/memory-shaper-thumbnail.jpg",
    video: "/sma-demo-video.mov",
    videoCaption: "This was the resetting process of a Nitinol wire set in a square mold. Sharp angles are hard for Nitinol to recreate, but the general shape is there.",
    role: "ENGR 1020 Project",
    dates: "Jan 2026 - Apr 2026",
    problem:
      "UVA's Materials Science department uses shape-memory alloys to demonstrate a unique material property and draw students into materials science. The existing demonstrations worked but relied on heavy heating elements and liquid cooling, making them hard to move. We set out to build a single-person, portable module that lets a user set a Nitinol wire into a shape, then safely heat and cool it to watch the shape-memory effect repeat reliably without liquid cooling or exposed hazards.",
    designDecisions: [
      "Soapstone molding for the wire: the Nitinol sits in a non-conductive soapstone mold that absorbs radiant heat, keeping the hot element away from the user. An exposed-wire approach would have been faster to build but not safe enough for live demonstrations.",
      "Decoupled heating and cooling circuits: heating runs on a 12V supply and cooling runs separately on a 9V, battery-powered Arduino-controlled fan circuit, each switched independently through high-current switches. Splitting them meant each circuit could be sized and controlled for its own job without compromising on a shared supply.",
      "Wind tunnel with a flow-restricting stand: the soapstone mold sits on a stand that narrows the tunnel cross-section, speeding up the airflow over the wire for faster cooling.",
      "PLA casing + acrylic viewing panel: keeps the module lightweight and easy to reprint, and the acrylic window gives a clear view of the wire transforming without exposing anyone to the heat.",
    ],
    iterations: [
      "The cooling configuration wasn't obvious up front, so we ran CFD to compare options instead of guessing. Two fan arrangements were modeled: a \"push-pull\" layout (both fans driving air the same direction) and a \"turbulent\" layout (fans facing inward). Push-pull cooled the wire in roughly half the time, so that became the final design.",
      "Resistive heating brought high-temperature and electrical hazards to design around, and delayed/missing parts plus limited fabrication and testing time forced prioritization of the core heating-and-cooling module.",
    ],
    futureWork: [
      "Implement a feedback control loop with real-time temperature sensing via thermocouple, feeding a controller that drives the heating and cooling circuits automatically.",
      "With live temperature as the feedback signal, the system could hold the wire at a target setpoint, cut heating before overshooting toward unsafe temperatures, and trigger the cooling fans on its own.",
      "In the current build, heating and cooling are switched manually and the wire temperature is estimated rather than measured.",
    ],
    tools: [
      "SolidWorks",
      "SolidWorks Flow Simulation (CFD)",
      "Arduino-controlled cooling circuit",
      "High-current switching",
      "12V heating / 9V cooling supplies",
      "Nitinol wire",
      "Soapstone mold",
      "PLA enclosure (3D printed)",
      "Acrylic viewing panel",
    ],
    results: [
      "Push-pull cooling reached room temperature in about half the time of the alternative layout.",
      "On heating, the wire returned to its set shape nearly instantaneously, reaching an estimated ~500°C in roughly 5 seconds.",
      "The final module handles the full set-heat-return cycle repeatedly and is portable enough for one person to carry and set up.",
    ],
    resultImages: [
      { src: "/sma-cfd-screenshot.png", alt: "SolidWorks CFD simulation results" },
      { src: "/sma-fan-graph.png", alt: "Push-pull vs turbulent fan configuration comparison" },
    ],
    teamNote: "4-person team in ENGR 1020 @ UVA. I handled CFD analysis and case modeling to get the cooling right.",
    links: [
      { label: "GitHub", url: "https://github.com/GabrielRhys/Shape-Memory-Alloy-Demonstrator" },
      { label: "Project Poster (PDF)", url: "/sma-poster.pdf" },
    ],
  },
  {
    id: "inverted-pendulum",
    title: "Single Inverted Pendulum with LQR Control",
    tag: "Personal",
    summary:
      "A cart-mounted pendulum that balances upright and rejects disturbances in real time using a full state-space model and Linear Quadratic Regulator feedback.",
    oneLiner:
      "A cart-mounted pendulum that balances upright and rejects disturbances in real time, using a full state-space model and Linear Quadratic Regulator feedback rather than classical PID.",
    thumbnail: "/pendulum-thumbnail.jpg",
    video: "/pendulum-demo.mp4",
    videoCaption: "Single pendulum reacting to a manual disturbance and returning back to equilibrium.",
    role: "Personal Project",
    dates: "May 2026 - Jul 2026",
    problem:
      "I wanted to implement a real control system from scratch, starting from the physics and ending with hardware that actually balances. The goal was a cart-mounted inverted pendulum that holds itself upright and recovers from manual pushes, controlled by an LQR rather than a PID loop so I could work through state-space modeling and optimal control firsthand.",
    designDecisions: [
      "Full nonlinear derivation before linearizing: rather than starting from a linearized textbook model, I derived the system's equations of motion using Lagrangian mechanics and only linearized about the upright equilibrium afterward. This meant the state-space model was traceable back to real physical parameters (cart mass, pendulum mass, length, friction).",
      "Hand-tuned K over Q/R optimization: instead of relying purely on Q/R weighting through MATLAB's lqr() solver, I tuned the feedback gain matrix K directly against the linearized model, trading off responsiveness, control effort, and cart travel range by hand. This gave more direct control over the specific tradeoffs I cared about than an indirect Q/R search would have.",
      "AS5600 magnetic encoder for angle feedback: chosen for low-latency, low-weight, and zero added friction angle measurement over a rotary encoder mounted at the pivot.",
      "GT2 belt-drive cart: kept the actuation mechanism low-backlash and repeatable for consistent control response. Powered by a 30:1 metal DC geared-down motor.",
    ],
    iterations: [
      "Initial hand-tuned gains were too aggressive and caused oscillation near equilibrium. Iterating down the position gain resolved the overshoot without sacrificing recovery speed.",
      "The original wiring setup caused the pendulum to lose some range of motion. Creating a circular hub on the cart for the wires to route and rotate with the pendulum allowed full, uninterrupted motion.",
    ],
    tools: [
      "MATLAB (state-space modeling, LQR gain derivation)",
      "Lagrangian mechanics (equations of motion derivation)",
      "ELEGOO ESP32",
      "AS5600 Magnetic Encoder w/ TCA9548A I2C Multiplexer",
      "CQRobot 30:1 Metal DC Geared-down Motor w/ BTS7960 Driver",
      "GT2 belt drive",
      "SolidWorks",
      "Bambu P1S 3D Printer",
    ],
    results: [
      "Recovers from an 8° manual disturbance in approximately 6 seconds.",
      "Holds steady-state error within 2.3° (std. dev = 0.9°).",
      "Soldering and circuitry, press-fit tolerance design in SolidWorks, analyzing and optimizing equations in MATLAB, and implementing control theory on a physical system.",
    ],
    resultImages: [
      { src: "/pendulum-cad-front.png", alt: "SolidWorks front view of the cart-pendulum assembly" },
      { src: "/pendulum-cad-iso.png", alt: "SolidWorks isometric view showing motor, belt drive, and pendulum arm" },
      { src: "/pendulum-build-4.jpg", alt: "Close-up of the 3D-printed cart, magnetic encoder housing, and GT2 belt" },
      { src: "/pendulum-build-3.jpg", alt: "Cart-pendulum system during testing with code running on a laptop" },
    ],
    futureWork: [
      "Add swing-up control to handle arbitrary starting angles rather than requiring manual setup near equilibrium.",
      "Extend the same Lagrangian/state-space/LQR pipeline to the double inverted pendulum, a significantly more complex underactuated system, which became the next project in this progression.",
    ],
    teamNote: "Solo project. I derived the full dynamics, built the state-space model, and implemented and tuned the LQR controller.",
    links: [
      { label: "GitHub", url: "https://github.com/GabrielRhys/Single-Inverted-Pendulum-w-LQR" },
    ],
  },
];

export const education = [
  {
    org: "University of Virginia — School of Engineering and Applied Science",
    role: "B.S. Mechanical Engineering · Class of 2029",
    date: "Aug 2025 — May 2029",
    logo: "/uva-logo.png",
    bullets: [
      "GPA: 3.67 / 4.00",
      "Certified SolidWorks Associate — Associate Level",
      "Active member — UVA Solar Car Team (Suspension)",
    ],
  },
];

export const coursework = [
  "Intro to Engineering (ENGR 1020)",
  "Calculus",
  "Physics",
  "Chemistry",
  "Engineering Design",
  "Statics (upcoming)",
  "Dynamics (upcoming)",
  "Thermodynamics (upcoming)",
];

export const experience = [
  {
    org: "UVA Data Science Research Group",
    role: "Research Contributor — Connectographics",
    date: "May 2024 — Nov 2024",
    logo: "/uva-logo.png",
    bullets: [
      "Developed a user interface to generate connectograms from diffusion MRI data",
      "Co-authored published research poster at F1000Research on brain structure differences",
      "Processed patient datasets into multi-layered circular connectivity graphs",
    ],
  },
];

export const leadership = [
  {
    org: "UVA Solar Car Team",
    role: "Suspension Team Member",
    date: "Feb 2026 — Present",
    logo: "/uva-logo.png",
    bullets: [
      "Fabricated trailing-arm suspension parts using water-jet and plasma cutting",
      "Led sourcing of specialized spring products by analyzing manufacturer data for optimal spring rates and dimensions",
      "Aided the transition from design calculations to final vehicle assembly",
    ],
  },
];

export const honors = [
  {
    title: "Published Poster — F1000Research",
    date: "Nov 2024",
    note: "Seng G et al. · MYO1E & white matter microstructure in ASD",
  },
  {
    title: "Certified SolidWorks Associate",
    date: "2025",
    note: "Dassault Systèmes — Associate Level",
  },
];

export const posts = [
  {
    id: "week-one-suspension",
    title: "Week One on the Solar Car Suspension Team",
    date: "2026-02-20",
    excerpt:
      "A spring rate is only as honest as the load case you chose. Notes from my first week on the suspension sub-team — what I expected, what surprised me, and the calculations I had to redo.",
    cover:
      "https://images.unsplash.com/photo-1559971236-486f8ff10906?w=1400&q=85",
    readTime: "5 min read",
  },
  {
    id: "cfd-nitinol",
    title: "What CFD Taught Me About a 450°C Wire",
    date: "2026-04-10",
    excerpt:
      "The Push-Pull configuration dropped 428°C in under a minute. Here's why the Turbulent setup — which looked better on paper — came in second.",
    cover:
      "https://images.unsplash.com/photo-1642969164999-979483e21601?w=1400&q=85",
    readTime: "6 min read",
  },
  {
    id: "from-research-to-shop",
    title: "From a Research Poster to the Machine Shop",
    date: "2026-03-15",
    excerpt:
      "A year ago I was processing MRI datasets into connectograms. Today I'm running a plasma cutter. The thread between them is narrower than you'd think.",
    cover:
      "https://images.unsplash.com/photo-1764114440403-4dd539cb582a?w=1400&q=85",
    readTime: "4 min read",
  },
];

export const subjectOptions = [
  { value: "internship", label: "Internship Inquiry" },
  { value: "research", label: "Research Collaboration" },
  { value: "club", label: "Club / Team" },
  { value: "general", label: "General" },
];

export const socials = {
  linkedin: "https://www.linkedin.com/in/gabrielseng/",
  github: "https://github.com/gabrielseng",
  email: "gabeseng@gmail.com",
};
